// Amazon's title-length cap, effective 2026-07-27. Portfolio Health's title
// rubric (scoring-engine-constants.ts) was reconciled 2026-09-29 to use this
// same 75-char threshold, so both rubrics agree on title length now.
export const TITLE_CHAR_LIMIT = 75;

/**
 * Soft FLOOR for a rewritten title. Below this the title path does one "expand"
 * pass to use more of the 75-char cap with search-relevant descriptors drawn
 * from the source (never marketing fluff). NOT a hard guarantee — a title stays
 * short if there is genuinely nothing search-relevant to add. ~73% of the cap.
 *
 * Also the Portfolio Health title pass floor and the Alexa proxy title gate
 * (SCORING_CONFIG in scoring-engine-constants.ts derives both from this), so a
 * rewrite that clears this floor is never marked down by the tool’s own
 * grader. Raising it raises the bar for generation AND grading together.
 *
 * Intentionally a single lowest-common-denominator floor, not a per-category
 * guarantee — the per-category target bands in `CATEGORY_MAP`
 * (ai-optimize-utils.ts) run higher (e.g. hardlines ~60-75) and are enforced
 * only via prompt guidance, not this code check. A title can pass this floor
 * while still landing below its own category's stated target.
 */
export const TITLE_TARGET_MIN = 55;

/**
 * Soft TARGET the title path GENERATES toward, and the trigger for the "expand"
 * pass. Deliberately higher than `TITLE_TARGET_MIN`, which stays the GRADING
 * floor — the two were one number until 2026-10-19 and that coupling is exactly
 * why output plateaued in the mid-50s: a 56-char title cleared the floor, so the
 * expand pass never fired, and Portfolio Health still paid it full credit.
 *
 * Splitting them lets generation aim high without the grader failing the tool's
 * own output. The invariant that matters is `TITLE_TARGET_MIN <= this <=
 * TITLE_CHAR_LIMIT` (asserted in title-band-consistency.test.ts): the grader
 * must never be STRICTER than the generator, but it may be more lenient.
 *
 * Raising this raises only the bar for generation. Raising `TITLE_TARGET_MIN`
 * raises generation AND grading together — that is the one to leave alone.
 */
export const TITLE_GENERATION_TARGET = 70;

export const TitleStatus = {
  COMPLIANT: 'compliant',
  NEEDS_UPDATE: 'needs-update',
  MISSING: 'missing'
} as const;
export type TitleStatusType = (typeof TitleStatus)[keyof typeof TitleStatus];

export const STATUS_RANK: Record<TitleStatusType, number> = {
  [TitleStatus.NEEDS_UPDATE]: 0,
  [TitleStatus.MISSING]: 1,
  [TitleStatus.COMPLIANT]: 2
};

/**
 * Row order in the compliance table.
 *
 * SUBMITTED is the default: the table silently re-ordered results worst-first,
 * so a user who pasted a list of ASINs got back a differently-ordered list and
 * could not reconcile it against their source sheet. Both ingest paths preserve
 * submission order (the ASIN lookup writes results by input index, the CSV
 * parser maps rows in file order), so the original order is always available.
 *
 * OVER_CAP_FIRST keeps the old triage view as an explicit choice rather than an
 * invisible default.
 */
export const TitleSortMode = {
  SUBMITTED: 'submitted',
  OVER_CAP_FIRST: 'over-cap-first'
} as const;
export type TitleSortModeType = (typeof TitleSortMode)[keyof typeof TitleSortMode];

/**
 * Amazon's new PDP format pairs the ≤75-char title with a separate item-detail /
 * "item highlight" field, capped at 125 characters (counting spaces), same
 * auto-truncate/suppress risk as the title. NET-NEW field the scraper never
 * captured — we only ever classify text we generated.
 */
export const ITEM_HIGHLIGHT_CHAR_LIMIT = 125;

export const HighlightStatus = {
  COMPLIANT: 'compliant',
  OVER: 'over',
  EMPTY: 'empty'
} as const;
export type HighlightStatusType = (typeof HighlightStatus)[keyof typeof HighlightStatus];
